import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AutoRefreshControl, SLOW_REFRESH_INTERVALS, useRefreshInterval,
} from '@/components/ui/auto-refresh-control'
import { cn } from '@/lib/utils'
import { formatDurationMinutes as elapsed, formatEffortMinutes as effort } from '@/lib/formatDuration'
import { postRecoveryApi, type PostRecoveryRun, type SetOverview } from './api'

/**
 * Every run in a recovery set, planned against actual.
 *
 * THE COVERAGE IS THE STORY, NOT A FOOTNOTE. When this was built the whole database held three
 * measured task durations, and a run of fifteen tasks had two. So a run's `actualMinutes` is the
 * sum of the handful of tasks somebody timed, and setting that beside the run's FULL planned
 * effort produces a headline like "40% under plan" that is pure arithmetic on a missing thirteen
 * tasks. The only honest comparison is the server's `varianceMinutes` (and `plannedMinutesOfMeasured`
 * in the totals), which are computed over the measured tasks alone — so this screen never
 * subtracts one column from another to invent a variance of its own.
 *
 * An unmeasured run is an EM DASH, never a zero. Zero is a measurement that came back as no
 * difference; a dash is the absence of one, and rendering the second as the first is how a table
 * of two data points comes to look like a table of fifteen.
 *
 * Outliers are shown, not dropped. Two of the historical measurements are abandoned timers — a
 * task left open for seven weeks against a five-minute plan. They stay in the figures because
 * this payload carries no way to subtract them, and they are called out loudly wherever they land,
 * which is the difference between a caveated number and a quietly wrong one.
 */

type SortKey = 'variance' | 'coverage'
type SortDir = 'asc' | 'desc'

const coverageOf = (r: PostRecoveryRun) => (r.taskCount > 0 ? r.tasksMeasured / r.taskCount : 0)

export function RunVarianceTable({ setId, selectedRequestId, onSelectRun }: {
  setId: number
  selectedRequestId: number | null
  onSelectRun: (requestId: number) => void
}) {
  const { t } = useTranslation()

  // Default OFF: a set is mostly read after the fact, and a measurement only lands when a person
  // moves a task — there is nothing here that changes on a ten-second cadence.
  const [refresh, setRefresh] = useRefreshInterval(
    'vs-post-recovery-set-refresh', false, SLOW_REFRESH_INTERVALS,
  )

  const { data, isLoading, isError, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['post-recovery-set', setId],
    queryFn: () => postRecoveryApi.setOverview(setId),
    refetchInterval: refresh,
  })

  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'variance', dir: 'desc' })

  const runs = useMemo(() => {
    const list = [...(data?.runs ?? [])]
    const dir = sort.dir === 'asc' ? 1 : -1
    // Object then request as the tiebreak, so the order is stable across refetches rather than
    // shuffling rows under the cursor of whoever is reading it.
    const then = (a: PostRecoveryRun, b: PostRecoveryRun) =>
      a.objectId.localeCompare(b.objectId) || a.requestId - b.requestId

    return list.sort((a, b) => {
      if (sort.key === 'variance') {
        // Nulls last in BOTH directions. A run with nothing measured has no variance, and letting
        // it sort in among the measured rows as though it were zero puts "on plan" next to rows
        // that were actually timed.
        if (a.varianceMinutes == null && b.varianceMinutes == null) return then(a, b)
        if (a.varianceMinutes == null) return 1
        if (b.varianceMinutes == null) return -1
        return (a.varianceMinutes - b.varianceMinutes) * dir || then(a, b)
      }
      return (coverageOf(a) - coverageOf(b)) * dir || then(a, b)
    })
  }, [data, sort])

  const totals = data?.totals ?? null
  const toggle = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }))

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{t('postRecovery.runsHeading', 'Runs in this recovery set')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('postRecovery.runsSubtitle',
                 'What each run was planned to take, what was actually measured, and how much of it was measured at all.')}
            </p>
          </div>
          <AutoRefreshControl
            value={refresh}
            onChange={setRefresh}
            onRefresh={() => { void refetch() }}
            isRefreshing={isFetching}
            updatedAt={dataUpdatedAt || null}
            intervals={SLOW_REFRESH_INTERVALS}
          />
        </div>

        {isLoading && (
          <div className="rounded-xl border p-8 text-center text-sm text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </div>
        )}

        {isError && !isLoading && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-rose-500/40 bg-rose-500/5 p-4 text-sm">
            <span>{t('postRecovery.loadFailed', 'The runs for this set could not be read.')}</span>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => void refetch()}>
              {t('common.retry', 'Try again')}
            </Button>
          </div>
        )}

        {!isLoading && !isError && data && (
          data.runs.length === 0 ? (
            <EmptySet notRunCount={totals?.notRunCount ?? 0} />
          ) : (
            <>
              {totals ? <Totals totals={totals} /> : (
                <p className="text-xs text-muted-foreground">
                  {t('postRecovery.noTotals', 'Set totals were not returned, so only the runs themselves are shown.')}
                </p>
              )}

              <div className="overflow-x-auto rounded-xl border">
                {/* border-separate, deliberately: box-shadow on a table row is undefined under
                    border-collapse, and the selected row is marked with a RING rather than a fill
                    so that selection cannot be mistaken for a status colour. The row rules come
                    from the cells instead. */}
                <table className="w-full min-w-[1040px] border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <Th align="left">{t('postRecovery.colObject', 'Object')}</Th>
                      <Th>{t('postRecovery.colTasks', 'Tasks')}</Th>
                      <SortTh
                        label={t('postRecovery.colCoverage', 'Measured')}
                        active={sort.key === 'coverage'} dir={sort.dir}
                        onToggle={() => toggle('coverage')}
                      />
                      <Th hint={t('postRecovery.colPlannedHint', 'all tasks')}>
                        {t('postRecovery.colPlanned', 'Planned effort')}
                      </Th>
                      <Th hint={t('postRecovery.colActualHint', 'measured tasks only')}>
                        {t('postRecovery.colActual', 'Actual effort')}
                      </Th>
                      <SortTh
                        label={t('postRecovery.colVariance', 'Variance')}
                        help={t('postRecovery.varianceHelp',
                                'Actual minus planned for the measured tasks only, computed like for like. Positive means longer than planned. It is never the actual column minus the planned column — those two cover different tasks.')}
                        active={sort.key === 'variance'} dir={sort.dir}
                        onToggle={() => toggle('variance')}
                      />
                      <Th>{t('postRecovery.colWaiting', 'Waiting')}</Th>
                      <Th align="left">{t('postRecovery.colStatus', 'Status')}</Th>
                      <Th align="left">{t('postRecovery.colOutliers', 'Outliers')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <Row
                        key={r.requestId}
                        run={r}
                        selected={selectedRequestId === r.requestId}
                        onSelect={() => onSelectRun(r.requestId)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}
      </div>
    </TooltipProvider>
  )
}

/* ---------------------------------------------------------------------- summary strip */

function Totals({ totals }: { totals: NonNullable<SetOverview['totals']> }) {
  const { t } = useTranslation()
  const measuredAnything = totals.runsMeasured > 0 && totals.tasksMeasured > 0

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Stat
          tint="bg-sky-50 dark:bg-sky-950/40"
          label={t('postRecovery.statRuns', 'Runs')}
          value={String(totals.runCount)}
          detail={totals.notRunCount > 0
            ? t('postRecovery.statRunsNotRun', '{{n}} more objects in the set were never run', { n: totals.notRunCount })
            : t('postRecovery.statRunsAllRun', 'every object in the set has a run')}
        />
        <Stat
          tint="bg-emerald-50 dark:bg-emerald-950/40"
          label={t('postRecovery.statFinished', 'Finished')}
          value={String(totals.runsFinished)}
          detail={t('postRecovery.statFinishedDetail', 'of {{n}} runs', { n: totals.runCount })}
        />
        <Stat
          tint="bg-violet-50 dark:bg-violet-950/40"
          label={t('postRecovery.statPlanned', 'Planned effort')}
          value={effort(totals.plannedMinutes)}
          detail={t('postRecovery.statPlannedDetail', 'the whole plan — all {{n}} tasks', { n: totals.tasksTotal })}
        />
        {/* THE CARD THIS SCREEN GETS WRONG IF NOBODY IS CAREFUL. The actual is the sum of the
            measured tasks, so it is quoted against plannedMinutesOfMeasured — the plan for those
            SAME tasks — and never against plannedMinutes on the card to its left. And with nothing
            measured it says so: a "0h" here reads as a recovery that took no time at all. */}
        <Stat
          tint="bg-amber-50 dark:bg-amber-950/40"
          label={t('postRecovery.statActual', 'Measured actual effort')}
          value={measuredAnything
            ? effort(totals.actualMinutes)
            : t('postRecovery.statActualNone', 'Nothing measured yet')}
          small={!measuredAnything}
          detail={measuredAnything
            ? t('postRecovery.statActualDetail',
                'the {{m}} measured tasks only, against {{planned}} planned for those same tasks · {{runs}} of {{total}} runs have any measurement',
                { m: totals.tasksMeasured, planned: effort(totals.plannedMinutesOfMeasured),
                  runs: totals.runsMeasured, total: totals.runCount })
            : t('postRecovery.statActualNoneDetail',
                'durations are recorded when an engineer moves a task to Started before completing it')}
          warn={measuredAnything && totals.tasksOutlier > 0
            ? t('postRecovery.statActualOutliers', 'includes {{n}} flagged outlier task(s)', { n: totals.tasksOutlier })
            : undefined}
        />
        {/* Waiting is a SUM ACROSS TASKS, not one span of wall clock, so it is formatted like
            effort — the same reason effort never rolls into days. */}
        <Stat
          tint="bg-rose-50 dark:bg-rose-950/40"
          label={t('postRecovery.statWaiting', 'Waiting')}
          value={totals.waitingMinutes > 0
            ? effort(totals.waitingMinutes)
            : t('postRecovery.statWaitingNone', 'None recorded')}
          small={!(totals.waitingMinutes > 0)}
          detail={t('postRecovery.statWaitingDetail', 'summed across tasks held at a wait/go')}
        />
        <Stat
          tint="bg-teal-50 dark:bg-teal-950/40"
          label={t('postRecovery.statCoverage', 'Coverage')}
          value={t('postRecovery.statCoverageValue', '{{m}} of {{n}}', { m: totals.tasksMeasured, n: totals.tasksTotal })}
          tone={totals.tasksMeasured === 0 ? 'text-muted-foreground' : undefined}
          detail={t('postRecovery.statCoverageDetail', 'tasks have a measured duration')}
        />
      </div>

      {/* What the figures above rest on, said before anyone reads a variance out of the table.
          With this little measurement the caveat IS the result, not a disclaimer under it. */}
      <div className="space-y-1.5 rounded-xl border bg-muted/20 p-3">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t('postRecovery.restsHeading', 'What these figures rest on')}
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {totals.tasksMeasured === 0
            ? t('postRecovery.restsNothing',
                'No task in this set has a measured duration yet, so every actual and variance below is empty. A duration is recorded when an engineer moves a task to Started and then completes it — a task closed without ever being started leaves nothing to measure.')
            : t('postRecovery.restsPartial',
                '{{m}} of {{n}} tasks have a measured duration, across {{runs}} of {{total}} runs. Every actual and variance covers those tasks alone; the rest of each run is unmeasured, so a run\'s actual is not comparable with its full planned effort.',
                { m: totals.tasksMeasured, n: totals.tasksTotal, runs: totals.runsMeasured, total: totals.runCount })}
        </p>
        {totals.tasksOutlier > 0 && (
          <p className="flex items-start gap-1.5 text-xs leading-relaxed text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>
              {t('postRecovery.restsOutliers',
                 '{{n}} task(s) are flagged as outliers — a timer left open long after the work stopped, not work that took that long. They are EXCLUDED from the effort and variance figures above, and from the measured count beside them, so those numbers describe real work; each one is marked on its row.',
                 { n: totals.tasksOutlier })}
            </span>
          </p>
        )}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------------- rows */

function Row({ run, selected, onSelect }: {
  run: PostRecoveryRun
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  const noMeasurement = run.actualMinutes == null || run.varianceMinutes == null
  const pct = run.taskCount > 0 ? Math.round(coverageOf(run) * 100) : 0

  // aria-current, not aria-selected: aria-selected only means anything inside a grid, and this is
  // a plain table that happens to drive a detail panel beside it.
  return (
    <tr
      onClick={onSelect}
      aria-current={selected ? true : undefined}
      className={cn(
        'cursor-pointer transition-colors hover:bg-muted/40',
        // A ring, not a fill: every fill on this row already means something (status, variance,
        // outlier), and a seventh colour would be read as an eighth piece of evidence.
        selected && 'bg-muted/30 ring-2 ring-inset ring-primary',
      )}
    >
      <Td align="left">
        <div className="font-medium">{run.objectId}</div>
        {run.description ? (
          <div className="max-w-[280px] truncate text-[11px] text-muted-foreground">{run.description}</div>
        ) : null}
      </Td>

      <Td>
        <div className="tabular-nums">{run.tasksCompleted} / {run.taskCount}</div>
        {run.tasksIssue > 0 && (
          <div className="text-[11px] text-amber-700 dark:text-amber-400">
            {t('postRecovery.tasksIssue', '{{n}} with issues', { n: run.tasksIssue })}
          </div>
        )}
      </Td>

      <Td>
        <div className={cn('tabular-nums', run.tasksMeasured === 0 && 'text-muted-foreground')}>
          {t('postRecovery.statCoverageValue', '{{m}} of {{n}}', { m: run.tasksMeasured, n: run.taskCount })}
        </div>
        {run.tasksMeasured === 0 ? (
          <Chip tone="muted">{t('postRecovery.coverageNone', 'none measured')}</Chip>
        ) : run.tasksMeasured < run.taskCount ? (
          // Marked rather than hidden. A partially measured run is still evidence — it just is not
          // evidence about the whole run, and the row has to say which of the two it is.
          <Chip tone="amber">{t('postRecovery.coveragePartial', 'partial · {{pct}}%', { pct })}</Chip>
        ) : null}
      </Td>

      <Td className="tabular-nums">{effort(run.plannedMinutes)}</Td>

      <Td className={cn('tabular-nums', run.actualMinutes == null && 'text-muted-foreground')}>
        {run.actualMinutes == null ? '—' : effort(run.actualMinutes)}
      </Td>

      <Td>
        {noMeasurement ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="cursor-help text-muted-foreground">—</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[300px] text-xs">
              {run.tasksMeasured === 0
                ? t('postRecovery.varianceNoneTip',
                    'Nothing was measured on this run, so there is no variance — not a variance of zero. Durations are recorded when an engineer moves a task to Started before completing it.')
                : t('postRecovery.varianceUnavailableTip',
                    'This run has measured tasks but no comparable variance was returned for it, so nothing is shown rather than a figure derived here.')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                className={cn(
                  'cursor-help font-medium tabular-nums',
                  run.varianceMinutes! > 0 ? 'text-rose-700 dark:text-rose-400'
                    : run.varianceMinutes! < 0 ? 'text-emerald-700 dark:text-emerald-400'
                      : 'text-muted-foreground',
                )}
              >
                {signedEffort(run.varianceMinutes!)}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[320px] text-xs">
              {t('postRecovery.varianceRowTip',
                 'Over the {{m}} of {{n}} tasks that were measured. The other {{rest}} are not in this figure, so it does not describe the whole run.',
                 { m: run.tasksMeasured, n: run.taskCount, rest: Math.max(run.taskCount - run.tasksMeasured, 0) })}
              {run.tasksOutlier > 0 && ` ${t('postRecovery.varianceOutlierTip', '{{k}} further task(s) were measured but excluded as outliers.', { k: run.tasksOutlier })}`}
            </TooltipContent>
          </Tooltip>
        )}
      </Td>

      <Td className={cn('tabular-nums', run.waitingMinutes == null && 'text-muted-foreground')}>
        {run.waitingMinutes == null ? '—' : effort(run.waitingMinutes)}
      </Td>

      <Td align="left"><Status run={run} /></Td>

      <Td align="left">
        {run.tasksOutlier > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex cursor-help items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                <AlertTriangle className="h-3 w-3" aria-hidden />
                {t('postRecovery.outlierChip', '{{n}} outlier', { n: run.tasksOutlier })}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[300px] text-xs">
              {t('postRecovery.outlierTip',
                 'A measured duration far outside its plan — usually a timer left running rather than work that took that long. It is excluded from this row\u2019s effort and variance; open the run to see which task.')}
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </Td>
    </tr>
  )
}

function Status({ run }: { run: PostRecoveryRun }) {
  const { t } = useTranslation()
  const state = run.isFinished ? 'finished'
    : (run.actualStart || run.tasksCompleted > 0) ? 'running' : 'notStarted'

  const label = state === 'finished' ? t('postRecovery.statusFinished', 'Finished')
    : state === 'running' ? t('postRecovery.statusRunning', 'Running')
      : t('postRecovery.statusNotStarted', 'Not started')

  const tone = state === 'finished'
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
    : state === 'running'
      ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300'
      : 'bg-muted text-muted-foreground'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className={cn('cursor-help rounded px-1.5 py-0.5 text-[11px]', tone)}>{label}</span>
      </TooltipTrigger>
      {/* ELAPSED, and formatted as elapsed. The columns beside this one are person-time, which is
          why they are hours and never days — a run that took 26 hours end to end and a run that
          cost 26 hours of effort are different claims and must not look alike. */}
      <TooltipContent side="top" className="max-w-[300px] text-xs">
        {run.plannedElapsedMinutes == null
          ? t('postRecovery.elapsedNoPlan', 'No planned elapsed time is recorded for this run.')
          : t('postRecovery.elapsedPlanned', 'Planned to take {{p}} end to end.', { p: elapsed(run.plannedElapsedMinutes) })}
        {' '}
        {run.actualElapsedMinutes == null
          ? t('postRecovery.elapsedNoActual', 'How long it actually ran was not recorded.')
          : t('postRecovery.elapsedActual', 'It actually ran for {{a}}.', { a: elapsed(run.actualElapsedMinutes) })}
      </TooltipContent>
    </Tooltip>
  )
}

/* ---------------------------------------------------------------------- empty */

function EmptySet({ notRunCount }: { notRunCount: number }) {
  const { t } = useTranslation()
  return (
    <div className="rounded-xl border border-dashed p-8 text-center">
      <p className="text-sm font-medium">{t('postRecovery.emptyHeading', 'No runbook has been instantiated for this set yet')}</p>
      <p className="mx-auto mt-1 max-w-xl text-xs leading-relaxed text-muted-foreground">
        {t('postRecovery.emptyBody',
           'A run appears here once a runbook is created against a recovery request in this set. Until then there are no tasks, nothing planned, and nothing to measure an actual against.')}
      </p>
      {notRunCount > 0 && (
        <p className="mx-auto mt-2 max-w-xl text-xs text-muted-foreground">
          {t('postRecovery.emptyNotRun',
             '{{n}} objects are recorded against this set with no run at all — why each was skipped is set out in the not-run breakdown.',
             { n: notRunCount })}
        </p>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------- primitives */

/**
 * Signed, and a real minus rather than a hyphen so "−45m" cannot be read as the em dash that means
 * "not measured" two rows above it. Exactly on plan prints unsigned.
 */
function signedEffort(minutes: number): string {
  if (minutes === 0) return effort(0)
  return `${minutes > 0 ? '+' : '−'}${effort(Math.abs(minutes))}`
}

function Stat({ label, value, detail, tint, tone, small, warn }: {
  label: string
  value: string
  detail?: string
  tint: string
  tone?: string
  /** For a card whose value is a sentence rather than a figure — it must not shout like one. */
  small?: boolean
  warn?: string
}) {
  return (
    <div className={cn('rounded-xl border p-3', tint)}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-1 font-semibold tabular-nums', small ? 'text-base' : 'text-2xl', tone)}>{value}</div>
      {detail ? <div className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{detail}</div> : null}
      {warn ? (
        <div className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
          <span>{warn}</span>
        </div>
      ) : null}
    </div>
  )
}

function Chip({ tone, children }: { tone: 'amber' | 'muted'; children: React.ReactNode }) {
  return (
    <span className={cn(
      'mt-0.5 inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium',
      tone === 'amber'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
        : 'bg-muted text-muted-foreground',
    )}>
      {children}
    </span>
  )
}

function Th({ children, hint, align = 'right' }: {
  children: React.ReactNode
  hint?: string
  align?: 'left' | 'right'
}) {
  return (
    <th scope="col" className={cn('border-b px-3 py-2 font-medium', align === 'right' ? 'text-right' : 'text-left')}>
      <div>{children}</div>
      {hint ? <div className="text-[10px] normal-case tracking-normal text-muted-foreground">{hint}</div> : null}
    </th>
  )
}

/**
 * Sorting lives in the column header, where a table's sort belongs — this is not a view switcher,
 * so it is not a SegmentedControl and must not grow into a row of buttons above the table.
 */
function SortTh({ label, help, active, dir, onToggle }: {
  label: string
  help?: string
  active: boolean
  dir: SortDir
  onToggle: () => void
}) {
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="border-b px-3 py-2 text-right font-medium"
    >
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={onToggle}
          className={cn('inline-flex items-center gap-1 hover:text-foreground', active && 'text-foreground')}
        >
          {label}
          {active
            ? (dir === 'asc' ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden />)
            : <ArrowUpDown className="h-3 w-3 opacity-50" aria-hidden />}
        </button>
        {help ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="cursor-help"><Info className="h-3 w-3" aria-hidden /></span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[340px] text-xs normal-case tracking-normal">
              {help}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </th>
  )
}

function Td({ children, align = 'right', className }: {
  children: React.ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  return (
    <td className={cn('border-b px-3 py-2 align-top', align === 'right' ? 'text-right' : 'text-left', className)}>
      {children}
    </td>
  )
}
